import { getFramework, ResourceThumbnail } from '@/framework';
import { getLocaleStore } from '@/i18n/index';
import { base64Kit } from '@/utils/base64-kit';
import { deleteExtraInfo } from '@/utils/layer-kit';
import {
    LayerType,
    type IExportBehavior,
    type IImageProps,
    type ILayerBehavior,
    type ILayerOriginInfo,
    type ILayerProps,
    type ISize,
    type IWatermarkLayerProps,
} from '@/version-types';
import { EdgelessWatermarkLayer } from './edgeless-layer';
import { renderLayerToStage } from './render/render-to-stage';

function getBehavior(identity: string) {
    const layerBehavior: ILayerBehavior = {
        getEdgelessObject(layer: ILayerProps) {
            return new EdgelessWatermarkLayer(layer.id, getFramework(identity));
        },
        onBeforeAddOrUpdate(layer: ILayerProps) {
            const data = layer.data as IWatermarkLayerProps;
            data.url = base64Kit.getCode(data.url);
        },
        async onBeforeSaveAsync(layer: ILayerProps, _isAutoSave?: boolean) {
            const data = layer.data as IImageProps;
            deleteExtraInfo(layer);
            if (data.url) {
                data.url = await base64Kit.getRemoteUrlAsync(data.url);
            }
        },

        checkDisableCollision(layer: ILayerProps) {
            if ((layer.data as IWatermarkLayerProps).isFullScreen) return true;
            return false;
        },

        getScaleMinRatio(originInfo: ILayerOriginInfo, _layer: ILayerProps): number {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;

            return (width - offWidth) / width;
        },

        getScaleIsNeedLimitSize(): boolean {
            return true;
        },
        async getResultCanvasAsync(layer: ILayerProps, resultSize: ISize): Promise<HTMLCanvasElement | undefined> {
            return renderLayerToStage(layer, resultSize);
        },
        checkDisableInGroup(layer: ILayerProps): boolean {
            return true;
        },
        checkDisableActive(layer: ILayerProps): boolean {
            return (layer.data as IWatermarkLayerProps).isFullScreen;
        },
        checkTryPick(layer: ILayerProps): boolean {
            return !(layer.data as IWatermarkLayerProps).isFullScreen;
        },
        getMenuOverlapName(_layer: ILayerProps) {
            const { $t } = getLocaleStore();
            return $t((v) => v.layers.watermark);
        },
        getMenuOverlapIcon(layer: ILayerProps) {
            const resource = getFramework(identity).service.edgelessCanvas.getByLayerId(layer.id)?.resource;
            const { url } = layer.data as IWatermarkLayerProps;
            return <ResourceThumbnail resource={resource} fallbackUrl={base64Kit.getLocalUrl(url)} />;
        },
        checkDisableClone() {
            return true;
        },
        checkDisableCopy() {
            return true;
        },
        checkDisableCut() {
            return true;
        },
        checkDisableFlip() {
            return true;
        },
    };

    return layerBehavior;
}

const exportBehavior: IExportBehavior = {
    layerType: LayerType.WATERMARK,
    getBehavior,
};

export default exportBehavior;
